import { useCallback, useMemo } from 'react';
import { NavigationProp } from '@react-navigation/native';
import { useAppDispatch } from '@/store';
import { resetAuth } from '@/store/slices/authSlice';
import { useAdminLogoutMutation } from '@/store/api';
import { useAppSelector } from '@/store';
import { SecureStorage } from '@/services/shared/storage/SecureStorage';
import { AdminStackParamList } from '@/navigation/admin/AdminNavigator';
import { 
  createAdminNavigationHandler, 
  getScreenMetadata 
} from '@/utils/admin/navigation';

/**
 * Custom hook for admin screen logic
 * Provides reusable logic for admin screens including navigation and sign out
 */
export const useAdminScreen = (
  screenName: keyof AdminStackParamList,
  navigation?: NavigationProp<AdminStackParamList>
) => {
  const dispatch = useAppDispatch();
  const [adminLogoutMutation] = useAdminLogoutMutation();

  // Get screen metadata
  const screenMetadata = useMemo(() => getScreenMetadata(screenName), [screenName]);

  // Memoized navigation handler
  const handleNavigate = useMemo(
    () => createAdminNavigationHandler(navigation),
    [navigation]
  );

  // Default logo press handler
  const handleLogoPress = useCallback(() => {
    // TODO: Implement default logo press functionality
    console.log('Logo pressed');
  }, []);

  // Sign out handler with proper error handling
  const handleSignOut = useCallback(async () => {
    try {
      await adminLogoutMutation().unwrap();
      await SecureStorage.getInstance().clearTokens();
      // Reset Redux auth state so RootNavigator re-renders to Auth stack
      dispatch(resetAuth());
    } catch (error: any) {
      console.error('🚨 Admin sign out failed:', error);
      // Even if logout fails, RootNavigator will handle navigation based on auth state
    }
  }, [dispatch, adminLogoutMutation]);

  return {
    screenMetadata,
    handleNavigate,
    handleLogoPress,
    handleSignOut,
  };
};
